import React, { useRef, KeyboardEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import {
  SHORTCUTS_QUIZ_DEFAULT,
  shortcutsIds,
} from '../../constants/quizShortcuts';
import { PopupMenuItem } from '../PopupMenu';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { Button } from '@osmosis/react-ui/components/Button';
import { H3 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';
import useClickOutside from '../../hooks/useClickOutside';
import {
  SetShortcuts,
  useQuizShortcutsContext,
} from '../../context/QuizShortcutsContext';

export type Shortcuts = {
  [key: string]: string | null;
};

type PopupMenuShortcutsProps = PopupMenuItem & {
  isHeader?: boolean;
  shortcuts: Shortcuts;
  setShortcuts: SetShortcuts;
  text: string;
  id: string;
};

const confidenceGroup = [
  shortcutsIds.high,
  shortcutsIds.medium,
  shortcutsIds.low,
];

const accuracyGroup = [shortcutsIds.correct, shortcutsIds.incorrect];

// Shortcuts can be shared between these groups
// e.g. an item in accuracyGroup can have "1" and an item in confidenceGroup can also have "1"
const sharableSet: string[][] = [accuracyGroup, confidenceGroup];

/**
 * PopupMenuShortcuts.
 */
const PopupMenuShortcuts = (props: PopupMenuShortcutsProps) => {
  const { id, text, isHeader, setShortcuts, shortcuts } = props;
  const containerRef = useRef<HTMLElement>(null);

  const { shortcutActiveEditID, setShortcutActiveEditID } =
    useQuizShortcutsContext();

  useClickOutside(containerRef, () => {
    if (shortcutActiveEditID && !containerRef.current) {
      setShortcutActiveEditID(null);
    }
  });

  const handleReset = () => setShortcuts(SHORTCUTS_QUIZ_DEFAULT);

  const shortcutsWithIcons = (shortcut: string) => {
    switch (shortcut) {
      case 'ArrowRight':
        return <Icon icon={faArrowRight} />;
      case 'ArrowLeft':
        return <Icon icon={faArrowLeft} />;
      case ' ':
        return 'space';
      default:
        return shortcut;
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (shortcutActiveEditID === id) {
      let sharableGroups: string[][] = [];
      // If this id is in one a group that's in the sharable set
      if (sharableSet.find((group) => group.includes(id))) {
        // Filter out the group that contains this id, because we want it to be
        // sharable between groups, not within a group
        sharableGroups = sharableSet.filter((group) => !group.includes(id));
      }

      const updateShortcuts = Object.entries(shortcuts).map(([key, value]) => {
        if (
          !sharableGroups.find((g) => g.includes(key)) && // If it's not a sharable group
          value === event.key // If it matches the current id
        ) {
          // Unset shortcut
          value = null;
        }

        if (key === id) {
          // Update this key to the shortcut
          value = event.key;
        }

        return [key, value];
      });

      setShortcuts(Object.fromEntries(updateShortcuts));
      setShortcutActiveEditID(null);
    }
  };

  if (id === 'quiz.shortcuts.header.reset') {
    return (
      <Button
        onClick={handleReset}
        onKeyDown={(e) => {
          if ([' ', 'Enter'].includes(e.key)) handleReset();
        }}
        py="10px"
        px="7px"
        m="5px"
        variant="transparent"
        color="gray50"
        width="100%"
        fontWeight={400}
        fontSize={16}
        sx={{ borderRadius: '5px' }}
        data-testid={`${id}-btn`}
      >
        {text}
      </Button>
    );
  }

  if (isHeader) {
    return (
      <H3 fontSize={18} fontWeight={400} color="gray30" px="10px" py="7px">
        {text}
      </H3>
    );
  }

  return (
    <Button
      p={0}
      minWidth="220px"
      m="5px"
      variant="transparent"
      color="text"
      width="100%"
      onKeyDown={handleKeyDown}
      onClick={() => setShortcutActiveEditID(id)}
      mr="20px"
      sx={{
        justifyContent: 'left',
        display: 'flex',
        ':hover div .keyCode': {
          bg: '#2079df',
          color: 'white',
        },
      }}
      data-testid={`${id}-btn`}
      tabIndex={0}
    >
      <Flex
        ref={containerRef}
        width="100%"
        alignItems="center"
        sx={{ gap: 3, cursor: 'pointer' }}
        justifyContent="space-between"
        pl="6px"
      >
        <P
          fontWeight="bold"
          color="gray50"
          sx={{ whiteSpace: ['break-spaces', 'nowrap'] }}
        >
          {text}
        </P>
        {shortcutActiveEditID === id ? (
          <P
            px="16px"
            py="8px"
            m={0}
            fontSize={16}
            lineHeight="16px"
            fontWeight="bold"
            color="white"
            bg="green"
            sx={{ borderRadius: '5px' }}
          >
            <FormattedMessage
              id="quiz.shortcuts.pressakey"
              defaultMessage="Press a key..."
            />
          </P>
        ) : (
          <P
            className="keyCode"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px="16px"
            py="8px"
            lineHeight="16px"
            m={0}
            height="2em"
            fontSize={16}
            bg={shortcuts[id] === null ? 'yellow' : 'gray20'}
            fontWeight="bold"
            color="gray60"
            sx={{
              borderRadius: '5px',
              textTransform: 'uppercase',
            }}
          >
            {shortcutsWithIcons(shortcuts[id] || '')}
          </P>
        )}
      </Flex>
    </Button>
  );
};

export default PopupMenuShortcuts;
